/* 
Copyright (c) Karsten Jeske and John Firth
This source code is licensed under the proprietary license found in the
LICENSE file in the root directory of this source tree.
*/

import { SESSION_STORAGE_USER_PARAMETERS_KEY } from "constants/sessionStorageKeys"
import { TAB_IDS } from "constants/tabIds"

import { useLastSubmittedUserParameters } from "app/hooks"
import { RootState } from "app/store"
import { dismissFailureToast, showToast } from "components/AppToast"
import {
  computeInitialScrsRanges,
  setInitialScrsFresh,
} from "features/initialScrs/InitialScrsSlice"
import {
  applyFilterInitialization,
  buildInitializeFiltersPayload,
  selectFiltersInitialized,
  selectHistoricalDataFilters,
} from "features/results/HistoricalDataFiltersSlice"
import { selectUiTables } from "features/results/ResultsSlice"
import { submissionCommitted } from "features/userParameters/submissionCommitted"
import {
  CurrentUserParameters,
  currentUserParametersSchema,
} from "features/userParameters/UserParametersSchemata"
import { userParametersResolver } from "features/userParameters/userParametersResolver"
import _ from "lodash"
import { FC, ReactNode, SyntheticEvent, useCallback, useEffect, useRef, useState } from "react"
import { DeepPartial, FieldErrors, FormProvider, useForm, useWatch } from "react-hook-form"
import { useDispatch, useSelector, useStore } from "react-redux"
import { computeResults } from "services/computations"
import { fetchInitialScrs } from "services/fetching"
// AIDEV-NOTE: imported from the module rather than the "services/fetching" barrel on purpose.
// The barrel is the side-effectful fetch API and is mocked wholesale in tests; the request
// descriptors are pure data that a test stubbing the network still needs to be real, so a
// barrel import would resolve to `undefined` under those mocks.
import {
  INITIAL_SCRS_REQUEST,
  classifyFetchFailure,
  fetchErrorFingerprint,
  fetchErrorTags,
} from "services/fetching/requestContext"
import { Sentry } from "services/sentry"
import { countTotalErrors, createTabErrorMap, navigateToFirstErrorTab } from "utils/form/tabErrors"
import { DeepReadonly } from "utils/readonly"
import { safeSessionStorageRemoveItem, safeSessionStorageSetItem } from "utils/safeSessionStorage"

// AIDEV-NOTE: Keep form prop types stable for callers/tests while handling readonly-lint edge cases at callback boundaries.
export interface UserParametersFormProps {
  /* eslint-disable @typescript-eslint/prefer-readonly-parameter-types */
  children: (
    formMethods: ReturnType<typeof useForm<CurrentUserParameters>>,
    activeTab: string,
    setActiveTab: (tab: string) => void,
    tabErrorMap: Record<string, number>,
    loading: boolean,
    handleCalculateClick: (event: React.MouseEvent) => void,
    triggerCalculation: () => void,
    resetFromUpload: (data: CurrentUserParameters) => void
  ) => ReactNode
  /* eslint-enable @typescript-eslint/prefer-readonly-parameter-types */
  defaultValues: CurrentUserParameters
}

// AIDEV-NOTE: (#51) A stuck spinner throws nothing, so Sentry cannot currently see it at
// all - the reported bug has produced zero events. This bound is the reporting threshold
// for "loading has been true impossibly long". It is deliberately much shorter than the
// absolute worst legitimate case (about 97s under DEFAULT_FETCH_CONFIG, derived below)
// because that worst case always resolves on its own: once retries are exhausted, or a
// body-phase deadline fires, fetchInitialScrs throws, submitForm's catch shows an error toast,
// and loading clears normally. That is a slow failure, not a stuck one, so this bound doesn't
// need to clear it. What it must clear is every healthy request that retries at most once, so
// a request that's still legitimately retrying, or still legitimately downloading, doesn't get
// reported as stuck (#1568).
//
// The slowest such request is this sequence through runInitialScrsAttempts (initialScrs.ts),
// at DEFAULT_FETCH_CONFIG:
//
//   1. Attempt 0 gets a retryable 5xx whose headers land just inside the
//      header deadline (`timeout`).                                            10s
//   2. Its error body is read until ERROR_BODY_READ_TIMEOUT_MS cuts it off.     2s
//   3. The first backoff: 1000 * 2^1 plus up to 50% jitter, so 2-3s - not the
//      ~1.5s you get from reading 2^0 - because initialScrs.ts increments
//      `attempt` *before* calling calculateBackoff (see its retry branches).    3s
//   4. Attempt 1's headers land just inside the header deadline.               10s
//   5. Its 200 body keeps arriving and finishes just inside the total cap
//      (`bodyTotalTimeout`, #1554).                                            30s
//                                                                       total: 55s
//
// The other once-retried shape is a header-phase timeout (10s), then the 3s backoff, then 10s
// headers and a 30s body: 53s. A request that never retries takes at most 10s headers + 30s
// body = 40s. Only the last attempt can spend the body cap, because a body read that hits it
// is never retried (#478) and one that finishes ends the request. The body term is the 30s cap
// rather than the 10s idle deadline because a body that keeps arriving, however slowly, is
// healthy until the cap.
//
// 62s is 7s above that 55s ceiling (62 - 55), so a once-retried slow download is below the
// threshold even at its slowest, and the 53s and 40s shapes are further below it. Requests
// that retry twice or more are deliberately not cleared: the slowest twice-retried one runs
// 10 + 2 + 3 + 10 + 2 + 6 + 10 + 30 = 73s (the second backoff is 1000 * 2^2 plus up to 50%,
// so 4-6s), past this threshold. A wait that long is exactly the silent long wait #990 and
// #992 are about, so a report on one is the signal wanted, not noise.
//
// The absolute worst case is lower than four full attempts' worth of header wait plus body
// cap, because only the last attempt can spend the 30s body cap. Each of the three retried
// attempts takes at most 12s: a 5xx whose headers land just inside the 10s header deadline,
// then up to 2s reading its error body (ERROR_BODY_READ_TIMEOUT_MS). The last attempt takes
// at most 40s (10s headers + 30s body), and the backoffs are 2-3s, 4-6s and 8-12s. So
// 3 * 12s + 40s + 21s = ~97s.
//
// The 7s margin is against scheduling jitter, not against any longer legitimate wait. An
// earlier revision kept only ~2s, close enough that ordinary scheduling delay (the timer is
// armed a React commit before the fetch actually goes out, and a busy main thread can defer
// both the timer callback and the response handling) could put a legitimately-retrying
// request the wrong side of the line.
//
// The direction of the trade is still deliberate. Real results typically return in about a
// second, so a report firing during a rare multi-retry-but-eventually-fine request is
// cheap, informative noise, whereas a false negative - waiting so long that a truly stuck
// spinner sits unreported - is the more expensive failure mode. That is why this sits at
// 62s rather than being pushed out past the ~97s absolute worst case. See the test
// pinning this against the 55s sequence recomputed from DEFAULT_FETCH_CONFIG.
export const STUCK_SPINNER_REPORT_DELAY_MS = 62_000

// AIDEV-NOTE: (#991) What the user is told when the calculate request never came back: a
// dropped connection, a header-phase timeout, or a body-phase timeout (the bytes stopped, or
// were still arriving when the 30s cap ran out - #1554). Deliberately says nothing
// about calculation - none of the three ran one - and deliberately carries no `error.message`,
// whose wording is internal ("initial SCRs" appears nowhere in the interface) and names a
// subsystem the user cannot act on. "get the results from" rather than "reach": a body-phase
// timeout did reach the server, and the copy has to be true of all three shapes.
export const CONNECTIVITY_TOAST_TITLE = "Connection problem"
export const CONNECTIVITY_TOAST_MESSAGE =
  "We couldn't get the results from the server. Your connection may have dropped - check it and try again."

// This mapping is used by navigateToFirstErrorTab.ts
export const tabFieldMapping = {
  [TAB_IDS.GENERAL_PARAMETERS]: [
    "initialPortfolioDollars",
    "retirementStart",
    "retirementHorizonMonths",
    "finalValuePctOfInitial",
    "initialConsumptionPerAnnumDollars",
    "inflationPerAnnumPct",
    "allocationMode",
    "glidePathLengthMonths",
  ],
  [TAB_IDS.INITIAL_ASSETS]: [
    "initialAssets", // Add the root field to catch all initialAssets errors
    "initialAssets.expenseRatioPerAnnumPct",
    "initialAssets.allocation.spxTrPct",
    "initialAssets.allocation.worldExUsEquitiesPct",
    "initialAssets.allocation.cashPct",
    "initialAssets.allocation.bm10yPct",
    "initialAssets.allocation.bm30yPct",
    "initialAssets.allocation.commoditiesPct",
    "initialAssets.allocation.goldPct",
    "initialAssets.famaFrenchFactors.smallStocksPct",
    "initialAssets.famaFrenchFactors.valueStocksPct",
  ],
  [TAB_IDS.FINAL_ASSETS]: [
    "finalAssets", // Add the root field to catch all finalAssets errors
    "finalAssets.expenseRatioPerAnnumPct",
    "finalAssets.allocation.spxTrPct",
    "finalAssets.allocation.worldExUsEquitiesPct",
    "finalAssets.allocation.cashPct",
    "finalAssets.allocation.bm10yPct",
    "finalAssets.allocation.bm30yPct",
    "finalAssets.allocation.commoditiesPct",
    "finalAssets.allocation.goldPct",
    "finalAssets.famaFrenchFactors.smallStocksPct",
    "finalAssets.famaFrenchFactors.valueStocksPct",
  ],
  [TAB_IDS.CASH_FLOWS]: ["cashFlows"],
}

// AIDEV-NOTE: defaultValues is handed to useForm(), which requires a mutable
// object, so the props type cannot be made deeply readonly here. Previously this
// was hidden behind `(props: unknown)` plus a cast; the suppression is narrower
// and keeps the real prop types visible to callers.
// eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
export const UserParametersForm: FC<Readonly<UserParametersFormProps>> = ({
  children,
  defaultValues,
}) => {
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState(TAB_IDS.INSTRUCTIONS)
  const [tabErrorMap, setTabErrorMap] = useState<Record<string, number>>({})
  const lastSubmittedParameters = useLastSubmittedUserParameters()
  // AIDEV-NOTE: #474 removed the setLoading(false)-on-fresh effect that used to consume
  // `fresh`, but #471's stuck-spinner report (below) still wants it as diagnostic context
  // (resultsAreFresh), so the binding stays even though nothing else in this component
  // reads it now.
  const { uiTables, fresh: areUiTablesFresh } = useSelector(selectUiTables)
  // Read via the store directly (not useSelector) inside submitForm so we can see
  // filter/range state synchronously right after our own dispatches below, instead of
  // a render behind. useStore() returns whatever store the surrounding Provider
  // supplies, so this stays swappable in tests exactly like useSelector/useDispatch.
  const reduxStore = useStore<RootState>()

  const formMethods = useForm<CurrentUserParameters>({
    resolver: userParametersResolver,
    defaultValues,
    mode: "onChange", // Validates on change
    shouldUnregister: false,
  })

  const { control, trigger } = formMethods

  const allocationMode = useWatch({
    control,
    name: "allocationMode",
  })

  // Use a ref to track whether this is the first mount
  const usingInitialAllocationMode = useRef(true)

  // Monotonic id for each submission that actually reaches the network, so an awaited
  // response can tell whether it is still the one the user is waiting for.
  const submissionGenerationRef = useRef(0)

  // Add ref to track when form is being reset from upload
  const isResettingFromUpload = useRef(false)

  // Track the last data that was uploaded to detect when reset completes
  const lastUploadedData = useRef<CurrentUserParameters | null>(null)

  // Create the upload-specific reset function
  const resetFromUpload = useCallback(
    // AIDEV-NOTE: react-hook-form's reset() requires a mutable object, so this
    // parameter cannot be readonly. Typed honestly rather than taken as `unknown`.
    // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
    (data: CurrentUserParameters) => {
      isResettingFromUpload.current = true
      lastUploadedData.current = data
      formMethods.reset(data)
      // Don't reset the flag here - let the effect handle it
    },
    [formMethods]
  )

  // Watch only the allocationMode field directly rather than the whole form
  // to avoid unnecessary renders
  // Set up a specific effect for allocationMode changes
  useEffect(() => {
    // If we're resetting from upload, check if the reset is complete
    if (isResettingFromUpload.current && lastUploadedData.current) {
      // Check if the form now has the uploaded data (reset is complete)
      const currentValues = formMethods.getValues()
      const uploadedData = lastUploadedData.current

      if (
        currentValues.allocationMode === uploadedData.allocationMode &&
        currentValues.initialPortfolioDollars === uploadedData.initialPortfolioDollars
      ) {
        // Reset is complete, clear the flags
        isResettingFromUpload.current = false
        lastUploadedData.current = null
        return // Skip auto-population
      }
    }

    // Skip auto-population if we're still resetting from upload
    if (isResettingFromUpload.current) {
      return
    }

    // If allocation mode just changed to glide path (wasn't glide path before)
    if (allocationMode === "glide path") {
      // Get current form values
      const currentValues = formMethods.getValues()

      // Create a deep copy of initialAssets to avoid reference issues
      const initialAssetsCopy = _.cloneDeep(currentValues.initialAssets)

      // Set the finalAssets values with individual field updates to ensure proper rendering
      formMethods.setValue(
        "finalAssets.expenseRatioPerAnnumPct",
        initialAssetsCopy.expenseRatioPerAnnumPct
      )
      formMethods.setValue("finalAssets.allocation.spxTrPct", initialAssetsCopy.allocation.spxTrPct)
      formMethods.setValue(
        "finalAssets.allocation.worldExUsEquitiesPct",
        initialAssetsCopy.allocation.worldExUsEquitiesPct
      )
      formMethods.setValue("finalAssets.allocation.cashPct", initialAssetsCopy.allocation.cashPct)
      formMethods.setValue("finalAssets.allocation.bm10yPct", initialAssetsCopy.allocation.bm10yPct)
      formMethods.setValue("finalAssets.allocation.bm30yPct", initialAssetsCopy.allocation.bm30yPct)
      formMethods.setValue(
        "finalAssets.allocation.commoditiesPct",
        initialAssetsCopy.allocation.commoditiesPct
      )
      formMethods.setValue("finalAssets.allocation.goldPct", initialAssetsCopy.allocation.goldPct)
      formMethods.setValue(
        "finalAssets.famaFrenchFactors.smallStocksPct",
        initialAssetsCopy.famaFrenchFactors.smallStocksPct
      )
      formMethods.setValue(
        "finalAssets.famaFrenchFactors.valueStocksPct",
        initialAssetsCopy.famaFrenchFactors.valueStocksPct
      )

      // Show toast notification only if it's not the first mount
      if (!usingInitialAllocationMode.current) {
        showToast({
          type: "info",
          title: "Glide Path Mode Selected",
          message:
            "The initial and final asset values have been set to the previous static asset values.",
        })
      }
    } else {
      // If not in glide path mode, unregister the fields that aren't needed
      formMethods.unregister("finalAssets")
      formMethods.unregister("glidePathLengthMonths")

      // Only trigger validation when fields are unregistered
      void trigger()

      // Show toast notification only if it's not the first mount
      if (!usingInitialAllocationMode.current) {
        showToast({
          type: "info",
          title: "Static Mode Selected",
          message:
            "The asset values have been set to the previous glide path's initial asset values.",
        })
      }
    }

    // After the first use of this effect, set isFirstMount to false
    // so subsequent changes will show toast notifications
    if (usingInitialAllocationMode.current) {
      usingInitialAllocationMode.current = false
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allocationMode, formMethods])

  const formValues = useWatch({ control })

  // Add an effect to check if results are fresh
  useEffect(() => {
    // we don't want changes to lastSubmittedParameters
    // that occur while loading to mark the results as fresh and thereby
    // clear 'loading' indicators prematurely
    if (!loading) {
      const currentParsedParams = currentUserParametersSchema.safeParse(formValues)

      const fresh =
        currentParsedParams.success && _.isEqual(lastSubmittedParameters, currentParsedParams.data)

      dispatch(setInitialScrsFresh(fresh))
    }
  }, [formValues, dispatch, lastSubmittedParameters, loading])

  // AIDEV-NOTE: (#51, #467/#474) Detection only - this deliberately does NOT clear
  // `loading`. #474 removed the only other clearing path that used to exist here (an
  // `areUiTablesFresh`-watching effect, which cleared `loading` on any results-fresh
  // transition regardless of which submission it belonged to - itself one of #467's two
  // race mechanisms). Now `submitForm`'s own `finally` is the sole clearing path, so a
  // report firing here means no submission has reached that `finally` for the whole
  // window - not that any single submission has been running that long. The effect
  // depends on `loading` alone, so a chain of overlapping submissions keeps one timer
  // running from the *first* one: a report can name a generation that started a second
  // ago. `submissionsStillInFlight` below is what distinguishes the two cases, and is
  // why it is in the payload. Reports on elapsed spinner time alone and attaches the
  // surrounding state, so a
  // report can be read as either a stranded flag or a still-running calculation whose
  // results simply predate it - which is the distinction the bug report leaves open.
  const submissionsStartedRef = useRef(0)
  const concurrentSubmissionsRef = useRef(0)

  // AIDEV-NOTE: Plain values from render (areUiTablesFresh, uiTables, lastSubmittedParameters,
  // activeTab) cannot be read directly inside the setTimeout callback below: the effect only
  // re-runs when `loading` changes, so its closure would freeze them at the instant `loading`
  // became true, not their state STUCK_SPINNER_REPORT_DELAY_MS later when the report actually
  // fires - silently defeating the report's whole purpose (telling a stranded flag apart from a
  // calculation that's still genuinely running). Refs updated every render, mirroring
  // submissionsStartedRef/concurrentSubmissionsRef above, keep the callback reading live state.
  const latestContextRef = useRef({
    areUiTablesFresh,
    uiTables,
    lastSubmittedParameters,
    activeTab,
  })
  latestContextRef.current = { areUiTablesFresh, uiTables, lastSubmittedParameters, activeTab }

  useEffect(() => {
    if (!loading) {
      return
    }
    const timeoutId = setTimeout(() => {
      const context = latestContextRef.current
      Sentry.captureMessage("Calculate spinner still active after expected completion", {
        level: "warning",
        tags: { errorType: "stuck_spinner", component: "UserParametersForm" },
        extra: {
          // The threshold that fired, not a measured duration: this timer fires once and
          // is never re-armed, so it cannot distinguish a spinner stuck for 25s from one
          // stuck for five minutes. Named for what it actually is to stop the value being
          // read as an elapsed time.
          reportThresholdMs: STUCK_SPINNER_REPORT_DELAY_MS,
          resultsAreFresh: context.areUiTablesFresh,
          resultsRendered: context.uiTables !== null,
          hasLastSubmittedParameters: context.lastSubmittedParameters !== null,
          submissionsStarted: submissionsStartedRef.current,
          submissionsStillInFlight: concurrentSubmissionsRef.current,
          activeTab: context.activeTab,
        },
      })
    }, STUCK_SPINNER_REPORT_DELAY_MS)

    return () => {
      clearTimeout(timeoutId)
    }
    // Re-arming the timer whenever context changes would reset the clock and hide exactly
    // the case being measured - only `loading` should restart it. Everything else is read
    // live from latestContextRef inside the callback instead, which is why this effect has
    // no other dependencies to declare.
  }, [loading])

  // A shared function for successful submission.
  const submitForm = async (userParameters: DeepReadonly<CurrentUserParameters>) => {
    // Skip submission if data hasn't changed and results already exist.
    //
    // AIDEV-NOTE: (#467/#474) The `!loading` term is load-bearing. Since #474,
    // `lastSubmittedParameters` means "the params of the last submission that *completed*",
    // not "the last submission that *started*" - so on its own this comparison answers
    // "do we already have results for these params?" while ignoring "is a different
    // submission about to replace them?". Without `!loading`, submitting P while a
    // submission for P' is still in flight is skipped as a no-op, and P' then lands and
    // wins, leaving the form showing P and the results showing P' - one plan's parameters
    // displayed alongside another plan's numbers. Requiring nothing to be in flight makes
    // the newest submission win instead: it claims a generation and supersedes P'.
    // Reachable via triggerCalculation (the xlsx-upload entry point), which is ungated by
    // design - the Calculate button is already disabled once `loading` has committed.
    //
    // AIDEV-NOTE: (#481) A consequence of the above: this guard only reflects *completed*
    // submissions and does not deduplicate against an in-flight one. Two identical
    // submissions that overlap - reachable only via triggerCalculation, e.g. uploading the
    // same xlsx twice while the first calculation is still running - both reach the
    // network instead of the second being skipped, because `lastSubmittedParameters` isn't
    // updated until the first one's fetch resolves. This is correctness-neutral (the
    // generation token above discards the superseded response) and costs one redundant
    // POST /api/v2/compute-initial-scrs in an obscure path. Deliberately not fixed by
    // tracking the in-flight params separately: that adds parallel state on exactly the
    // axis whose desync caused #467, to save one request in a rare scenario.
    // AIDEV-NOTE: (#991) Above the early return, not below it, and this position is the fix
    // rather than an accident of ordering. An error toast reports an attempt that failed, and
    // the user pressing Calculate - or Try again, which reads the form and can therefore land
    // on parameters that already have results - spends that message whichever branch runs. Put
    // below the guard, the one route that returns early left a *pinned* connection toast on
    // screen with nothing able to remove it, sitting over correct results indefinitely: submit
    // P, edit to P', watch P' fail, undo the edit, press Calculate. Success and info toasts are
    // untouched - see dismissFailureToast.
    dismissFailureToast()

    if (!loading && lastSubmittedParameters && _.isEqual(userParameters, lastSubmittedParameters)) {
      if (uiTables) {
        setActiveTab(TAB_IDS.RESULTS)
      }
      return
    }

    // AIDEV-NOTE: Generation token sequencing (#467). Nothing stops two submissions
    // overlapping - triggerCalculation (the xlsx-upload entry point) isn't gated on
    // `loading`, and the Calculate button's disabled={loading} lags a React commit behind
    // setLoading(true). So each real submission claims a generation, and only the newest
    // generation is allowed to touch `loading` or the store once its fetch settles. A
    // superseded response is dropped outright rather than cancelled: see #467's discussion
    // of why AbortController-based cancellation was deliberately left out.
    submissionGenerationRef.current += 1
    const thisGeneration = submissionGenerationRef.current

    // AIDEV-NOTE: (#51) Counted with a ref, not state, because `disabled={loading}` only
    // takes effect after React commits - a submission started inside that window, or via
    // triggerCalculation (XlsxUpload), bypasses the button guard entirely. These counts
    // exist purely as context for the stuck-spinner report above, which uses
    // submissionsStillInFlight to tell a stranded `loading` flag apart from a calculation
    // that is genuinely still running. Every increment below has a matching decrement on
    // every return path out of submitForm, including the generation-staleness early
    // returns - a superseded submission is no longer "in flight" from the user's
    // perspective the moment it's superseded, not just once its own promise settles.
    // No "Overlapping calculate submissions detected" alert fires here (#467, #474):
    // #474's generation token makes an overlap ordinary, handled behavior - a user editing
    // a field and resubmitting mid-request - not a sign of a bug, so alerting on every
    // overlap would just fire on routine usage.
    submissionsStartedRef.current += 1
    concurrentSubmissionsRef.current += 1

    setLoading(true)

    let initialScrsTable
    try {
      initialScrsTable = await fetchInitialScrs(userParameters)
    } catch (error) {
      // A superseded request's failure is not the user's current concern, and the newer
      // submission now owns `loading` - so report nothing and leave `loading` alone.
      if (thisGeneration !== submissionGenerationRef.current) {
        concurrentSubmissionsRef.current -= 1
        return
      }

      // AIDEV-NOTE: the decrement and setLoading(false) sit in a `finally` for the same
      // reason the success path's do: a throw from Sentry.captureException or showToast
      // between here and the end of the block would otherwise leak the counter
      // permanently, and `submissionsStillInFlight` would overstate in-flight work in
      // every later stuck-spinner report - silently corrupting the one field that tells a
      // stranded flag apart from a running calculation.
      try {
        // AIDEV-NOTE (#368): only this catch tags the endpoint. It covers `fetchInitialScrs`
        // and nothing else, whereas the catch further down covers `computeResults`, which
        // performs no request - tagging that one too would attribute a pure-computation bug to
        // an endpoint and make the tag useless for exactly the triage it exists for.
        // AIDEV-NOTE (#973): the fingerprint is spread in rather than passed as a key, so a
        // failure that is not a network-layer one carries no `fingerprint` at all and keeps
        // Sentry's own grouping. `fetchErrorFingerprint` decides which is which - see the note
        // on it for why every error here must not share one group.
        const fingerprint = fetchErrorFingerprint(error, INITIAL_SCRS_REQUEST)

        // AIDEV-NOTE: (#991) A connectivity failure is not a calculation failure, and until
        // this branch existed both the toast and the Sentry tag said it was. The tag matters
        // for triage as much as the toast does for the user: `calculation_error` was applied
        // to failures where no calculation was attempted, so the bucket filled with
        // connectivity faults and a genuine calculation defect arriving in it was materially
        // less likely to be noticed. Everything unrecognised keeps the old tag and the old
        // message - an unfamiliar failure must not be relabelled a network fault on no
        // evidence.
        const isConnectivityFailure = classifyFetchFailure(error) !== "unclassified"

        Sentry.captureException(error, {
          tags: {
            errorType: isConnectivityFailure ? "connectivity_error" : "calculation_error",
            component: "UserParametersForm",
            ...fetchErrorTags(INITIAL_SCRS_REQUEST),
          },
          ...(fingerprint ? { fingerprint } : {}),
        })

        if (isConnectivityFailure) {
          showToast({
            type: "error",
            title: CONNECTIVITY_TOAST_TITLE,
            message: CONNECTIVITY_TOAST_MESSAGE,
            // Recovery without finding the form again and re-entering it.
            //
            // AIDEV-NOTE: (#991 review) Runs `triggerCalculation`, not `submitForm` with the
            // parameters that failed. The toast is pinned, so it can sit on screen while the
            // user edits a field - and replaying the captured parameters would then submit a
            // plan the form no longer shows, commit it as `lastSubmittedParameters` and jump
            // to Results with figures for values the user cannot see. Going through
            // `handleSubmit` reads the form at the moment of the click and validates it, so an
            // edit is honoured and an invalid one raises the ordinary "fix N errors" toast.
            //
            // Read through a ref because this closure outlives the render that created it, and
            // the guards inside submitForm read `loading` and `lastSubmittedParameters` from
            // their own render's closure - a retry pressed a minute later must use the current
            // ones, not the ones that were true when the request failed.
            action: {
              label: "Try again",
              onAction: () => {
                triggerCalculationRef.current()
              },
            },
          })
        } else {
          // Surface the specific failure reason (e.g. a malformed/NaN-laden API response) when
          // available, rather than a single generic message for every cause. This only applies
          // to fetchInitialScrs errors: computeResults below is handled by its own catch, so an
          // unrelated runtime error there can't leak a raw JS message.
          const detail = error instanceof Error ? error.message : undefined

          showToast({
            type: "error",
            title: "Calculation Error",
            message: detail
              ? `Failed to calculate results: ${detail}`
              : "Failed to calculate results. Please try again.",
          })
        }
      } finally {
        concurrentSubmissionsRef.current -= 1
        setLoading(false)
      }
      return
    }

    // Discard a stale response before it can reach computeResults or the store: a newer
    // submission is in flight and owns both the results and `loading` from here on.
    if (thisGeneration !== submissionGenerationRef.current) {
      concurrentSubmissionsRef.current -= 1
      return
    }

    try {
      // Stored here, not before the fetch, so the parameters and the SCR data computed from
      // them only ever change together, for a generation already confirmed to be the winner.
      // On the success path, readers that pair the two independently - notably Results.tsx's
      // filter-driven recompute effect - therefore never observe a mismatched pair. Keeping it
      // inside this try also means a throw here can't strand `loading` (#469), since the
      // finally below covers it.
      //
      // AIDEV-NOTE: (#1061, #1125) Invariant: a throw from anywhere in this block leaves the
      // store exactly as it was before this submission, every slice still describing the
      // previous one. Two things make that true for every throw site, not only the first:
      //
      // 1. Nothing is stored until everything is derived. The ranges (which reject a
      //    non-finite year, drawdown or CAEY), the filters and the results (which reject a
      //    non-finite c1, sum_of_ct, sum_of_ctpt or initial_scr) are all computed from the
      //    fetched table before the single dispatch below.
      // 2. That dispatch is one action, which Redux applies wholly or not at all. The serify
      //    middleware replaces the payload with its serified copy before passing the action
      //    on, and throws on a value it cannot serify, such as NaN, before any reducer has run
      //    (@karmaniverous/serify-deserify 2.0.11, dist/index.mjs:107-110, createReduxMiddleware;
      //    the throw is serify's, :101). A slice reducer that throws aborts combineReducers
      //    before it returns the next root state, and dispatch assigns that state and notifies
      //    subscribers only after the reducer has returned (redux 5.0.1, dist/redux.mjs:309-324,
      //    combination; :158-165, dispatch).
      //
      // Only the subscriber notification runs after the state is assigned. The app subscribes
      // only through react-redux, whose handlers catch a throwing selector and re-render
      // rather than rethrow (react-dom 19.3.1, checkIfSnapshotChanged), so no throw reaches the
      // catch below from there with the commit already applied.
      //
      // The pairing is load-bearing because `lastSubmittedParameters` means "the parameters of
      // the submission that completed" (see the early return above). Recording a failed one
      // would make the form match it, and that early return would then swallow the retry.
      const stateBeforeCommit = reduxStore.getState()
      const computedRanges = computeInitialScrsRanges(initialScrsTable)

      // On a user's first-ever submission the filters still hold their pre-initialized
      // defaults, and Results.tsx - which normally initializes them reactively, once mounted -
      // is not mounted yet, because Calculator.tsx only mounts <Results /> once results exist.
      // Computing against the defaults and letting Results.tsx's own "Initialize filters"
      // effect supersede them was a double computation, and the literal cause of a
      // render-twice/flicker bug (#294). So the initialization is decided here, from the
      // store's live state (not the selector values captured at last render, which would be
      // stale within this call), and the results are computed against the filters it yields.
      // The same payload goes into the commit, so filtersInitialized is already true when
      // Results.tsx mounts and its effect no-ops instead of computing a second time.
      const initializeFiltersPayload = selectFiltersInitialized(stateBeforeCommit)
        ? null
        : buildInitializeFiltersPayload(computedRanges)
      const filtersForCompute =
        initializeFiltersPayload === null
          ? selectHistoricalDataFilters(stateBeforeCommit)
          : applyFilterInitialization(
              stateBeforeCommit.historicalDataFilters,
              initializeFiltersPayload
            ).filters

      // Named distinctly from the component-scope `uiTables` destructured from
      // selectUiTables above: that binding is still read by this function (the
      // unchanged-parameters early return) and by the stuck-spinner report's context, so
      // shadowing it here made the two easy to confuse when editing.
      const computedUiTables = computeResults(userParameters, filtersForCompute, initialScrsTable)

      dispatch(
        submissionCommitted({
          parameters: userParameters,
          initialScrsTable,
          initializeFilters: initializeFiltersPayload,
          results: computedUiTables,
        })
      )
      setActiveTab(TAB_IDS.RESULTS)
    } catch (error) {
      Sentry.captureException(error, {
        tags: { errorType: "calculation_error", component: "UserParametersForm" },
      })

      showToast({
        type: "error",
        title: "Calculation Error",
        message: "Failed to calculate results. Please try again.",
      })
    } finally {
      concurrentSubmissionsRef.current -= 1
      setLoading(false)
    }
  }

  // A shared error handler if validation fails.
  const onErrors = (errors: FieldErrors<CurrentUserParameters>) => {
    const errorMap = navigateToFirstErrorTab(errors, setActiveTab)
    setTabErrorMap(errorMap)

    const errorCount = countTotalErrors(errorMap)
    showToast({
      type: "error",
      title: "Form Validation Error",
      message: `Please fix ${errorCount} ${errorCount === 1 ? "error" : "errors"} before submitting.`,
    })
  }

  // This version is used for button clicks
  const handleCalculateClick = (event: React.MouseEvent) => {
    event.preventDefault()
    void formMethods.handleSubmit(submitForm, onErrors)(event)
  }

  // Public method to trigger calculation programmatically without an event
  const triggerCalculation = () => {
    void formMethods.handleSubmit(submitForm, onErrors)()
  }

  // AIDEV-NOTE: (#991) Reassigned on every render, like latestContextRef above and for the same
  // reason: the retry action handed to a toast is invoked long after the render that built it,
  // and everything it reaches - the form's current values, `loading`, `lastSubmittedParameters`
  // - is captured per render. Calling the instance the failing render built would re-run the
  // submission against a stale view of all three.
  const triggerCalculationRef = useRef(triggerCalculation)
  triggerCalculationRef.current = triggerCalculation

  // Initialize validation on mount and set up subscription to form changes
  useEffect(() => {
    // Validate all fields when component mounts
    void trigger()

    // Set up subscription to form changes
    const subscription = formMethods.watch(
      (values: DeepReadonly<DeepPartial<CurrentUserParameters>>) => {
        safeSessionStorageSetItem(SESSION_STORAGE_USER_PARAMETERS_KEY, JSON.stringify(values))
        // Only trigger validation, don't update state directly here
        void trigger()
      }
    )

    // Cleanup subscription on unmount
    return () => {
      subscription.unsubscribe()
      safeSessionStorageRemoveItem(SESSION_STORAGE_USER_PARAMETERS_KEY)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Add real-time error tracking that updates as soon as errors change
  useEffect(() => {
    // Using formState directly to update the error map
    // This will only run when errors actually change
    const newErrorMap = createTabErrorMap(formMethods.formState.errors)
    setTabErrorMap(newErrorMap)
  }, [formMethods.formState.errors])

  return (
    <FormProvider {...formMethods}>
      <form
        id="user-parameters"
        onSubmit={(event: SyntheticEvent<HTMLFormElement>) => {
          void formMethods.handleSubmit(submitForm, onErrors)(event)
        }}
        style={{ display: "none" }}
      >
        {/* Hidden form for submission */}
      </form>

      {children(
        formMethods,
        activeTab,
        setActiveTab,
        tabErrorMap,
        loading,
        handleCalculateClick,
        triggerCalculation,
        resetFromUpload
      )}
    </FormProvider>
  )
}
